"use client";

import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { Avatar, AvatarFallback, AvatarImage } from "@/shared/ui/data-display/avatar";
import { api } from "@/shared/lib/api";
import { tournamentKeys } from "@/shared/api/query-keys";

type ApiModerator = {
  userId: string;
  userDisplayName?: string | null;
  userAvatarUrl?: string | null;
  displayName?: string | null;
  avatarUrl?: string | null;
  role?: string | null;
  roleDisplayName?: string | null;
};

function getRoleLabel(
  mod: ApiModerator,
  tOverview: (k: string) => string,
  tPresets: (k: string) => string
): string {
  if (mod.roleDisplayName?.trim()) return mod.roleDisplayName.trim();
  const role = (mod.role ?? "").toString();
  if (role === "Moderator") return tOverview("roleModerator");
  if (role === "Admin") return tOverview("roleAdmin");
  if (role === "Owner") return tOverview("roleOwner");
  const presetLower = role.toLowerCase();
  if (presetLower.includes("head") || presetLower === "headreferee") return tPresets("headReferee");
  if (presetLower.includes("chat") || presetLower === "chatmoderator") return tPresets("chatModerator");
  if (presetLower === "caster") return tPresets("caster");
  return tPresets("custom");
}

export function TournamentAboutModeratorsBlock({ tournamentId }: { tournamentId: string }) {
  const tOverview = useTranslations("Tournaments.overview");
  const tPresets = useTranslations("Tournaments.moderators.presets");
  const { data: moderators = [], isLoading } = useQuery({
    queryKey: tournamentKeys.moderators(tournamentId),
    queryFn: async () => {
      try {
        const res = await api.get(`/tournaments/${tournamentId}/moderators`);
        const data = res.data as { moderators?: ApiModerator[] } | ApiModerator[];
        const list = Array.isArray(data) ? data : data?.moderators ?? [];
        return list as ApiModerator[];
      } catch (err: unknown) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 403 || status === 401) return [] as ApiModerator[];
        throw err;
      }
    },
    enabled: Boolean(tournamentId),
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 403 || status === 401) return false;
      return failureCount < 2;
    },
  });

  if (isLoading || moderators.length === 0) return null;

  return (
    <div className="mt-4 pt-4 border-t border-border">
      <div className="text-xs text-muted-foreground mb-2">{tOverview("moderatorsTitle")}</div>
      <ul className="flex flex-wrap gap-3">
        {moderators.map((mod) => {
          const name = mod.userDisplayName ?? mod.displayName ?? mod.userId;
          const avatarUrl = mod.userAvatarUrl ?? mod.avatarUrl ?? undefined;
          const roleLabel = getRoleLabel(mod, tOverview, tPresets);
          return (
            <li key={mod.userId} className="flex items-center gap-2">
              <Avatar className="h-8 w-8">
                <AvatarImage src={avatarUrl ?? undefined} alt={name} />
                <AvatarFallback className="text-sm">{name.charAt(0)}</AvatarFallback>
              </Avatar>
              <div className="flex flex-col min-w-0">
                <span className="font-medium text-sm text-foreground truncate">{name}</span>
                <span className="text-xs text-muted-foreground">{roleLabel}</span>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
